import {
  Box,
  Button,
  Card,
  CardActions,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useParams } from "react-router-dom";
import sendRequest from "../dataProvider/sendRequest";

const DownloadOtherPdf = ({ type }: { type: "loan_modify" }) => {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [message, setMessage] = React.useState("Preparing your download...");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [fileName, setFileName] = React.useState("Loan_Modification.pdf");
  const hasStartedDownload = React.useRef(false);

  const reqUrl = "/downloadOtherPdf/" + type + "/" + token;

  const extractFileName = (contentDisposition: string | null) => {
    if (!contentDisposition) {
      return "Loan Modification Agreement.pdf";
    }

    const utfFileNameMatch = contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/i
    );
    if (utfFileNameMatch?.[1]) {
      return decodeURIComponent(utfFileNameMatch[1]);
    }

    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    return "Loan_Modification.pdf";
  };

  const getErrorMessage = async (response: Response) => {
    const responseText = await response.text();

    try {
      const responseJson = JSON.parse(responseText);
      return responseJson?.message || "Download failed";
    } catch (_error) {
      return responseText || "Download failed";
    }
  };

  const triggerBrowserDownload = (blob: Blob, downloadName: string) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", downloadName);
    document.body.appendChild(link);
    link.click();

    window.setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    }, 100);
  };

  const handleDownload = React.useCallback(async () => {
    if (!token) {
      setLoading(false);
      setIsSuccess(false);
      setErrorMessage("Download link is invalid.");
      setMessage("Unable to start the download.");
      return;
    }

    setLoading(true);
    setIsSuccess(false);
    setErrorMessage("");
    setProgress(null);
    setMessage("Preparing your download...");

    try {
      const response = await sendRequest(reqUrl, null, "get", {
        rawResponse: true,
      });

      if (!(response instanceof Response)) {
        throw new Error("Download response is invalid.");
      }

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      const contentType = response.headers.get("content-type") || "";
      if (
        contentType.includes("application/json") ||
        contentType.includes("text/html")
      ) {
        throw new Error(await getErrorMessage(response));
      }

      const nextFileName = extractFileName(
        response.headers.get("content-disposition")
      );
      setFileName(nextFileName);
      setMessage("Downloading your file...");

      const contentLength = Number(response.headers.get("content-length") || 0);
      const reader = response.body?.getReader();

      if (!reader) {
        const blob = await response.blob();

        if (!blob.size) {
          throw new Error("File not found.");
        }

        triggerBrowserDownload(blob, nextFileName);
        setIsSuccess(true);
        setMessage("Download started successfully.");
        return;
      }

      const chunks: ArrayBuffer[] = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        if (value) {
          const normalizedChunk = new Uint8Array(value.byteLength);
          normalizedChunk.set(value);
          chunks.push(normalizedChunk.buffer);
          receivedLength += normalizedChunk.length;

          if (contentLength > 0) {
            setProgress(
              Math.min(100, Math.round((receivedLength / contentLength) * 100))
            );
          }
        }
      }

      const blob = new Blob(chunks, {
        type: contentType || "application/octet-stream",
      });

      if (!blob.size) {
        throw new Error("File not found.");
      }

      triggerBrowserDownload(blob, nextFileName);
      setIsSuccess(true);
      setProgress(100);
      setMessage("Download started successfully.");
    } catch (error: any) {
      setIsSuccess(false);
      setErrorMessage(error?.message || "Download failed.");
      setMessage("Unable to download the requested file.");
    } finally {
      setLoading(false);
    }
  }, [reqUrl, token]);

  React.useEffect(() => {
    if (hasStartedDownload.current) {
      return;
    }

    hasStartedDownload.current = true;
    void handleDownload();
  }, [handleDownload]);

  const titleText = isSuccess ? "DOWNLOAD READY" : "DOWNLOAD PDF";
  const descriptionText = isSuccess
    ? "Your file has been prepared and the browser download should begin automatically."
    : "We are preparing your PDF download. If your browser blocks it, use the button below to try again.";

  const actionLabel = loading
    ? "Downloading..."
    : isSuccess
    ? "Download Again"
    : "Retry Download";

  const progressText =
    typeof progress === "number" ? `${progress}% completed` : message;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#f2f7fa",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <Card
        sx={{ minWidth: 320, maxWidth: 520, marginTop: "6em", padding: "2em" }}
      >
        <Box
          sx={{
            margin: "1em",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img src="/images/happymango_logo.png" alt="logo" />
        </Box>
        <Box
          sx={{
            marginTop: "1em",
            display: "flex",
            justifyContent: "center",
            color: (theme) => theme.palette.grey[500],
          }}
        >
          {titleText}
        </Box>
        <Box
          sx={{
            marginTop: "1em",
            display: "flex",
            justifyContent: "center",
            color: (theme) => theme.palette.grey[500],
            textAlign: "center",
          }}
        >
          {descriptionText}
        </Box>
        <Box sx={{ padding: "0 1em 1em 1em", mt: 2 }}>
          <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
            {fileName}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 48,
            }}
          >
            {loading ? (
              typeof progress === "number" ? (
                <Box sx={{ width: "100%" }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}
                  >
                    {progressText}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={28} thickness={4} />
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {progressText}
                  </Typography>
                </Box>
              )
            ) : (
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: errorMessage ? "error.main" : "text.secondary",
                }}
              >
                {errorMessage || message}
              </Typography>
            )}
          </Box>
        </Box>
        <CardActions
          sx={{
            padding: "0 1em 1em 1em",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              void handleDownload();
            }}
            color="primary"
            disabled={loading}
            fullWidth
            sx={{
              backgroundColor: "#00ccb1",
              "&:hover": {
                backgroundColor: "#00b39b",
              },
            }}
          >
            {actionLabel}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default DownloadOtherPdf;
